import axios from 'axios';
import { safeStorage } from './safeStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests - supports Shopify session tokens and custom JWT
api.interceptors.request.use(async (config) => {
  // If inside Shopify embedded context, use App Bridge session token
  if (window.__SHOPIFY_EMBEDDED__ && window.shopify && window.shopify.idToken) {
    try {
      const sessionToken = await window.shopify.idToken();
      if (sessionToken) {
        config.headers.Authorization = `Bearer ${sessionToken}`;
        return config;
      }
    } catch (e) {
      // Fall through to stored token
    }
  }

  // Fallback: use stored JWT token (for direct login)
  const token = safeStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors + normalize Pydantic validation error arrays to a readable string
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.__SHOPIFY_EMBEDDED__) {
      safeStorage.removeItem('token');
      safeStorage.removeItem('user');
      window.location.href = '/login';
    }
    // FastAPI / Pydantic v2 returns `detail` as an array of validation error objects on 422.
    // Coerce to a single readable string so toast.error / JSX never receives an object child
    // (would otherwise trigger React error #31 "Objects are not valid as a React child").
    try {
      const d = error.response?.data?.detail;
      if (Array.isArray(d)) {
        error.response.data.detail = d.map((it) => {
          if (typeof it === 'string') return it;
          if (it && typeof it === 'object') return it.msg || JSON.stringify(it);
          return String(it);
        }).join('; ');
      } else if (d && typeof d === 'object') {
        error.response.data.detail = d.msg || JSON.stringify(d);
      }
    } catch (_) { /* never block error propagation */ }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

// Products
export const productsApi = {
  getAll: () => api.get('/products'),
  getOne: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Option Sets
export const optionSetsApi = {
  getAll: () => api.get('/option-sets'),
  getOne: (id) => api.get(`/option-sets/${id}`),
  create: (data) => api.post('/option-sets', data),
  update: (id, data) => api.put(`/option-sets/${id}`, data),
  delete: (id) => api.delete(`/option-sets/${id}`),
  getProducts: (id) => api.get(`/option-sets/${id}/products`),
  duplicate: (id) => api.post(`/option-sets/${id}/duplicate`),
};

// Orders — REMOVED (feature deprecated from merchant UI)

// Stats
export const statsApi = {
  get: () => api.get('/stats'),
};

// Price Calculator
export const priceApi = {
  calculate: (data) => api.post('/calculate-price', data),
};

// Conditional Logic
export const conditionsApi = {
  evaluate: (data) => api.post('/evaluate-conditions', data),
};

// Variant Maps
export const variantMapsApi = {
  getAll: (optionSetId) => api.get(`/variant-maps/${optionSetId}`),
  create: (data) => api.post('/variant-maps', data),
  update: (id, data) => api.put(`/variant-maps/${id}`, data),
  delete: (id) => api.delete(`/variant-maps/${id}`),
};

// Storefront (public-like endpoints)
export const storefrontApi = {
  getConfig: (productId) => api.get(`/storefront/product/${productId}`),
  calculatePrice: (data) => api.post('/storefront/calculate-price', data),
  addToCart: (data) => api.post('/storefront/add-to-cart', data),
};

// File Upload
export const uploadApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Shopify Integration
export const shopifyApi = {
  getProducts: () => api.get('/shopify/products'),
  getCollections: () => api.get('/shopify/collections'),
  getCollectionProducts: (collectionId) => api.get(`/shopify/collections/${collectionId}/products`),
  importProducts: (data) => api.post('/shopify/import', data),
  getImportProgress: (jobId) => api.get(`/shopify/import/progress/${jobId}`),
  connect: (shopDomain, accessToken) => api.post('/shopify/connect', null, {
    params: { shop_domain: shopDomain, access_token: accessToken }
  }),
  installWidget: () => api.post('/shopify/install-widget'),
  uninstallWidget: () => api.delete('/shopify/uninstall-widget'),
  getWidgetStatus: () => api.get('/shopify/widget-status'),
  getWebhooksStatus: () => api.get('/shopify/webhooks/status'),
  registerWebhooks: () => api.post('/shopify/webhooks/register'),
};

// Widget Config
export const widgetConfigApi = {
  get: () => api.get('/widget-config'),
  update: (data) => api.put('/widget-config', data),
};

// Billing
export const billingApi = {
  getPlan: () => api.get('/billing/plan'),
  subscribe: () => api.post('/billing/subscribe'),
  cancel: () => api.post('/billing/cancel'),
};

// Admin
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  getUsers: () => api.get('/admin/users'),
  updateUserPlan: (userId, plan) => api.put(`/admin/users/${userId}/plan`, { plan }),
};

// Bridge Products (auto-created add-on products for price modifiers)
export const bridgeProductsApi = {
  list: (includeOrders = false) => api.get('/bridge-products', { params: { include_orders: includeOrders } }),
  delete: (productId) => api.delete(`/bridge-products/${productId}`),
  cleanup: () => api.post('/bridge-products/cleanup'),
};

// Refresh product variants from Shopify (needed for older imports missing variant data)
export const refreshProductVariants = () => api.post('/products/refresh-variants');

// Auto-translate option set strings to multiple locales using LLM
export const translateOptionSet = (optionSetId, targetLocales, sourceLocale = 'auto') =>
  api.post('/translate-option-set', { option_set_id: optionSetId, target_locales: targetLocales, source_locale: sourceLocale });

// Save manually-edited translations for a single locale
export const saveOptionSetTranslations = (optionSetId, locale, entries) =>
  api.put(`/option-sets/${optionSetId}/translations`, { locale, entries });

// Translate a single text (for inline field-level translation)
export const translateSingleText = (text, targetLocale) =>
  api.post('/translate-text', { text, target_locale: targetLocale });

// Fetch configured locales from the merchant's Shopify admin (for translation suggestions)
export const getShopifyLocales = () => api.get('/shopify/locales');

// Bundles
export const bundlesApi = {
  list: () => api.get('/bundles'),
  get: (id) => api.get(`/bundles/${id}`),
  create: (data) => api.post('/bundles', data),
  update: (id, data) => api.put(`/bundles/${id}`, data),
  delete: (id) => api.delete(`/bundles/${id}`),
  syncDiscount: (id) => api.post(`/bundles/${id}/sync-discount`),
};

export default api;
